import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Package,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  Tag,
  X,
  CheckCircle,
  Clock,
  ShoppingBag,
  Filter,
  AlertCircle,
  Image,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';

const FacilityProducts = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [productForm, setProductForm] = useState({
    name: '',
    category: '',
    description: '',
    price: '',
    is_available: true,
    image_url: ''
  });
  
  const [newCategory, setNewCategory] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes, ordersRes] = await Promise.all([
        api.get('/products/my-products'),
        api.get('/products/categories'),
        api.get('/products/orders/facility')
      ]);
      
      setProducts(productsRes.data.products || []);
      setCategories(categoriesRes.data.categories || []);
      setOrders(ordersRes.data.orders || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    
    if (!productForm.name || !productForm.category || !productForm.price) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...productForm,
        price: parseFloat(productForm.price)
      };
      
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, payload);
        toast.success('Produit mis à jour');
      } else {
        await api.post('/products/', payload);
        toast.success('Produit créé');
      }
      
      setShowProductModal(false);
      setEditingProduct(null);
      setProductForm({ name: '', category: '', description: '', price: '', is_available: true, image_url: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Supprimer ce produit ?')) return;
    
    try {
      await api.delete(`/products/${productId}`);
      toast.success('Produit supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.trim()) return;
    
    try {
      await api.post(`/products/categories?name=${encodeURIComponent(newCategory)}`);
      toast.success('Catégorie créée');
      setNewCategory('');
      setShowCategoryModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Supprimer cette catégorie ?')) return;
    
    try {
      await api.delete(`/products/categories/${categoryId}`);
      toast.success('Catégorie supprimée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/products/orders/${orderId}/status?status=${status}`);
      toast.success('Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      category: product.category,
      description: product.description || '',
      price: product.price.toString(),
      is_available: product.is_available,
      image_url: product.image_url || ''
    });
    setShowProductModal(true);
  };

  const openNewModal = () => {
    setEditingProduct(null);
    setProductForm({ name: '', category: '', description: '', price: '', is_available: true, image_url: '' });
    setShowProductModal(true);
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'products');
      
      const response = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setProductForm({ ...productForm, image_url: response.data.url });
      toast.success('Image téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploadingImage(false);
    }
  };

  // Filter products
  const filteredProducts = products.filter(p => {
    const matchCategory = !filterCategory || p.category === filterCategory;
    const matchSearch = !searchQuery || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCategory && matchSearch;
  });

  // Stats
  const stats = {
    total: products.length,
    available: products.filter(p => p.is_available).length,
    pendingOrders: orders.filter(o => o.status === 'pending').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="facility-products-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Catalogue Produits & Services
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Gérez vos examens, médicaments et services
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCategoryModal(true)}
              data-testid="manage-categories-btn"
            >
              <Tag className="w-4 h-4 mr-2" />
              Catégories
            </Button>
            <Button
              onClick={openNewModal}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau produit
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total produits</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.available}</p>
                  <p className="text-xs text-slate-500">Disponibles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <ShoppingBag className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pendingOrders}</p>
                  <p className="text-xs text-slate-500">Commandes en attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-4">
          <TabsList>
            <TabsTrigger value="products" data-testid="products-tab">
              <Package className="w-4 h-4 mr-2" />
              Produits ({products.length})
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Commandes ({orders.length})
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher un produit..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-products-input"
                />
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
                data-testid="filter-category-select"
              >
                <option value="">Toutes les catégories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Products Grid */}
            {filteredProducts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-600">Aucun produit</h3>
                  <p className="text-slate-500 text-sm mt-1">
                    Commencez par ajouter vos produits et services
                  </p>
                  <Button onClick={openNewModal} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un produit
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts.map((product) => (
                  <Card key={product.id} className="hover:shadow-md transition-shadow overflow-hidden">
                    {product.image_url && (
                      <div className="h-32 bg-slate-100">
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900 line-clamp-1">{product.name}</h3>
                          <Badge variant="outline" className="mt-1 text-xs">
                            {product.category}
                          </Badge>
                        </div>
                        <Badge 
                          className={product.is_available 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-slate-100 text-slate-500'
                          }
                        >
                          {product.is_available ? 'Disponible' : 'Indisponible'}
                        </Badge>
                      </div>
                      
                      {product.description && (
                        <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                          {product.description}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between pt-3 border-t">
                        <p className="text-lg font-bold text-red-600">
                          {product.price.toLocaleString()} FCFA
                        </p>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteProduct(product.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            {orders.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-600">Aucune commande</h3>
                  <p className="text-slate-500 text-sm mt-1">
                    Les commandes de vos clients apparaîtront ici
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => (
                  <Card key={order.id}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{order.product_name}</h3>
                            <Badge variant="outline" className="text-xs">
                              x{order.quantity}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">
                            {order.patient_name} • {order.patient_phone}
                          </p>
                          {order.notes && (
                            <p className="text-sm text-slate-400 mt-1">Note: {order.notes}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-2">
                            {new Date(order.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-bold text-red-600">
                              {order.total_price.toLocaleString()} FCFA
                            </p>
                            <Badge className={
                              order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              order.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                              order.status === 'completed' ? 'bg-green-100 text-green-700' :
                              'bg-slate-100 text-slate-500'
                            }>
                              {order.status === 'pending' && 'En attente'}
                              {order.status === 'confirmed' && 'Confirmée'}
                              {order.status === 'completed' && 'Terminée'}
                              {order.status === 'cancelled' && 'Annulée'}
                            </Badge>
                          </div>
                          
                          {order.status === 'pending' && (
                            <div className="flex flex-col gap-1">
                              <Button
                                size="sm"
                                onClick={() => handleUpdateOrderStatus(order.id, 'confirmed')}
                                className="bg-green-500 hover:bg-green-600 text-xs"
                              >
                                Confirmer
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateOrderStatus(order.id, 'cancelled')}
                                className="text-xs"
                              >
                                Refuser
                              </Button>
                            </div>
                          )}
                          
                          {order.status === 'confirmed' && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateOrderStatus(order.id, 'completed')}
                              className="bg-blue-500 hover:bg-blue-600 text-xs"
                            >
                              Terminer
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
            </DialogTitle>
            <DialogDescription>
              {editingProduct ? 'Modifiez les informations du produit' : 'Ajoutez un nouveau produit ou service'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveProduct} className="space-y-4 mt-4">
            <div>
              <Label>Nom du produit/service *</Label>
              <Input
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                placeholder="Ex: Bilan sanguin complet"
                className="mt-1"
                data-testid="product-name-input"
              />
            </div>

            <div>
              <Label>Catégorie *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={productForm.category}
                  onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  placeholder="Ex: Examens de laboratoire"
                  list="categories-list"
                  className="flex-1"
                  data-testid="product-category-input"
                />
                <datalist id="categories-list">
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name} />
                  ))}
                </datalist>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Saisissez une catégorie existante ou créez-en une nouvelle
              </p>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                placeholder="Description détaillée..."
                className="mt-1"
                rows={3}
                data-testid="product-description-input"
              />
            </div>

            <div>
              <Label>Prix (FCFA) *</Label>
              <Input
                type="number"
                value={productForm.price}
                onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                placeholder="Ex: 15000"
                className="mt-1"
                min="0"
                data-testid="product-price-input"
              />
            </div>

            {/* Image Upload */}
            <div>
              <Label>Image du produit (optionnel)</Label>
              <div className="mt-1">
                {productForm.image_url ? (
                  <div className="relative">
                    <img 
                      src={productForm.image_url} 
                      alt="Aperçu" 
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setProductForm({ ...productForm, image_url: '' })}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                    {uploadingImage ? (
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400 mb-2" />
                        <span className="text-sm text-slate-500">Cliquez pour ajouter une image</span>
                        <span className="text-xs text-slate-400 mt-1">Max 5 Mo</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Disponibilité</Label>
                <p className="text-xs text-slate-500">Ce produit est-il disponible ?</p>
              </div>
              <Switch
                checked={productForm.is_available}
                onCheckedChange={(checked) => setProductForm({ ...productForm, is_available: checked })}
                data-testid="product-availability-switch"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowProductModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-red-500 hover:bg-red-600"
                data-testid="save-product-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingProduct ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gérer les catégories</DialogTitle>
            <DialogDescription>
              Créez et gérez vos catégories de produits
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Add new category */}
            <div className="flex gap-2">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Nouvelle catégorie..."
                onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
                data-testid="new-category-input"
              />
              <Button onClick={handleCreateCategory} disabled={!newCategory.trim()}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Existing categories */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {categories.length === 0 ? (
                <p className="text-center text-slate-500 py-4">Aucune catégorie</p>
              ) : (
                categories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-medium">{cat.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCategory(cat.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilityProducts;
